import React, { ReactNode, useEffect } from 'react';
import PermissionGate from '../../../../utils/hooks/PermissionGate';

export interface MenuItemType<T> {
  label: string;
  icon: ReactNode;
  permission: string[];
  onClick: (row: T) => void;
}

interface ContextMenuProps<T> {
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  row: T;
  menuItems: MenuItemType<T>[];
}

/**
 * Renders a context menu with a backdrop and disables page scrolling while open.
 */
export function ContextMenu<T>({
  anchorEl,
  onClose,
  row,
  menuItems,
}: Readonly<ContextMenuProps<T>>) {
  // Hide scroll on mount
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  if (!anchorEl) return null;

  const menuPosition = anchorEl.getBoundingClientRect();

  return (
    <>
      { /* Backdrop */ }
      <div
          className='fixed inset-0 bg-black/10 bg-opacity-25 z-40'
          onClick={onClose}
      />

      { /* Menu */ }
      <div
          className='fixed z-50 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-xl border border-gray-100 overflow-hidden min-w-[160px]'
          style={{
            top: menuPosition.top - 10,
            left: menuPosition.left - 175,
          }}
          role='menu'
      >
        <div className='p-1.5 space-y-0.5'>
          { menuItems.map((item) => (
            <PermissionGate
                requiredPermissions={item.permission}
                key={item.label}
            >
              <button
                  onClick={() => {
                    item.onClick(row);
                    onClose();
                  }}
                  className='w-full flex items-center gap-2.5 text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg focus:outline-none cursor-pointer whitespace-nowrap transition-colors duration-150 ease-in-out'
                  type='button'
                  role='menuitem'
              >
                <span className='text-gray-400 group-hover:text-gray-600'>
                  { item.icon }
                </span>
                { item.label }
              </button>
            </PermissionGate>
          )) }
        </div>
      </div>
    </>
  );
}
